import React, { useState, useEffect, useCallback } from "react";
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff } from "lucide-react";
import federationService from "../../../../services/host-apiServices/federationService";
import toastUtils from "../../../../utils/toast";

const ApiKeySection = ({ bindingId, capabilities = [], onKeyChange }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMintForm, setShowMintForm] = useState(false);
  const [label, setLabel] = useState("");
  const [minting, setMinting] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showFullKey, setShowFullKey] = useState(false);

  const fetchKeys = useCallback(async () => {
    if (!bindingId) return;
    try {
      const data = await federationService.listApiKeys(bindingId);
      setKeys(Array.isArray(data) ? data : []);
    } catch {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [bindingId]);

  useEffect(() => {
    fetchKeys();
  }, [bindingId, fetchKeys]);

  const handleMint = async e => {
    e.preventDefault();
    if (!label.trim()) return;
    setMinting(true);
    try {
      const result = await federationService.mintApiKey(bindingId, {
        capabilities: capabilities?.length ? capabilities : ["INVENTORY_FETCH"],
        label: label.trim(),
      });
      setNewKey(result);
      toastUtils.success("Access key created", {
        description: "Copy this key now — you won't be able to see it again.",
      });
      fetchKeys();
      onKeyChange?.();
      setLabel("");
    } catch (err) {
      toastUtils.error("Failed to create key", {
        description: err?.response?.data?.message || "Please try again.",
      });
    } finally {
      setMinting(false);
    }
  };

  const handleRevoke = async (keyId, keyLabel) => {
    if (!window.confirm(`Remove access key "${keyLabel}"? This cannot be undone.`)) return;
    try {
      await federationService.revokeApiKey(bindingId, keyId);
      toastUtils.success("Key removed");
      fetchKeys();
      onKeyChange?.();
    } catch {
      toastUtils.error("Failed to remove key");
    }
  };

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="h-20 bg-gray-100 rounded-lg animate-pulse" />;
  }

  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-900">Access Keys</h3>
        </div>
        {!showMintForm && (
          <button
            type="button"
            onClick={() => setShowMintForm(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            <Plus className="w-3 h-3" />
            Create key
          </button>
        )}
      </div>

      {showMintForm && (
        <form onSubmit={handleMint} className="p-4 bg-blue-50 border-b border-blue-100">
          <label className="block text-xs font-medium text-gray-700 mb-1">Name for this key</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Production API"
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button
              type="submit"
              disabled={minting || !label.trim()}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {minting ? "Creating..." : "Create"}
            </button>
            <button
              type="button"
              onClick={() => setShowMintForm(false)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {newKey && (
        <div className="p-4 border-b border-blue-100">
          <p className="text-xs font-medium text-gray-700 mb-2">Your new access key</p>
          <div className="flex items-center gap-2 mb-2">
            <code className="flex-1 px-3 py-2 text-xs bg-gray-900 text-green-400 rounded break-all font-mono">
              {showFullKey ? newKey.key : `${newKey.key?.substring(0, 20)}...`}
            </code>
            <button
              type="button"
              onClick={() => setShowFullKey(!showFullKey)}
              className="p-1.5 text-gray-500 hover:text-gray-700"
            >
              {showFullKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => copyToClipboard(newKey.key)}
              className="p-1.5 text-gray-500 hover:text-gray-700"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-amber-600">Save this key — it will not be shown again.</p>
        </div>
      )}

      {!keys.length && !newKey ? (
        <div className="p-6 text-center text-sm text-gray-400">No access keys yet</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {keys.map(k => (
            <div key={k.keyId} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 truncate">{k.label}</span>
                  <span className="text-xs text-gray-400">{k.prefix}...</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {k.lastUsedAt ? `Last used ${k.lastUsedAt.split("T")[0]}` : "Never used"}
                  {k.expiresAt ? ` · Expires ${k.expiresAt.split("T")[0]}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(k.keyId, k.label)}
                className="p-1.5 text-gray-400 hover:text-red-500"
                title="Remove key"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApiKeySection;
